import React, { useState } from 'react';

import { useNavigate, Link } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import { Tooltip, OverlayTrigger, Button } from 'react-bootstrap';
import { School, Discount } from 'app/types';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { formatMonth } from 'common/utils/conversions';
import { HighFace, FairFace, LowFace } from 'assets/explore';
import style from './SchoolCard.module.scss';

import { programLabelDetail } from 'common/utils';

import SchoolCardModal from './SchoolCardModal';

interface CompProps {
  school: School;
  appliedFilters: {
    [k: string]: any;
  };
}

const SchoolCard = ({ school }: CompProps): React.ReactElement => {
  const [showModal, setShowModal] = useState(false);
  const handleShowModal = () => setShowModal(true);

  const sendGTMEvent = (eventName: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'ga_event',
        eventName: eventName,
        schoolId: school.id,
        schoolName: school.name,
      },
    });
  };

  const discountLabel = () => {
    const applicationDiscountExists =
      school.discounts && school.discounts.find((d: Discount) => d.kind === 'application_fee');
    if (!applicationDiscountExists) return;
    return (
      <div className="triangle-container">
        <div className="triangle" />
        <div className="triangle-text">
          <span className="main-text">Discount </span>
          for early apply
        </div>
        <div className="rectangle" />
      </div>
    );
  };
  const schoolRanking = () => {
    const topRanked = school.school_rankings?.length && school.school_rankings[0].score <= 50;
    return (
      <div className="ranking-bg d-flex align-items-center justify-content-center">
        <span className="fw-600">
          {' '}
          {topRanked
            ? `${school.school_rankings[0].ranking.author} Top Ranked`
            : `${school.school_accreditations[0]?.accreditation.name || ''} Accredited`}
        </span>
      </div>
    );
  };
  const schoolCampuses = () => {
    let campuses = school.campuses.map((campus) => campus.location);
    campuses = [...new Set(campuses)];
    const moreThanThree = campuses.length > 3;
    const handleText = (text: string) => {
      if (moreThanThree && text.length > 49) {
        return `${campuses.slice(0, 2).join(' | ')} | +${campuses.length - 2} more`;
      } else {
        return text;
      }
    };
    if (moreThanThree) {
      return (
        <span className="text-white">
          {handleText(`${campuses.slice(0, 3).join(' | ')} | +${campuses.length - 3} more`)}
        </span>
      );
    } else {
      return <span className="text-white">{handleText(`${campuses.join(' | ')} `)}</span>;
    }
  };

  const intakeMonths = () => {
    const months = [...new Set(school?.intake_dates?.map((intake) => formatMonth(intake.date)))];
    if (months.length > 4) {
      return (
        <span className="text-center blue-text fw-semibold mb-0">{`${months
          .slice(0, 2)
          .join(' / ')} / +${months.length - 2} more`}</span>
      );
    } else {
      return (
        <span className="text-center blue-text fw-semibold mb-0">{`${months.join(' / ')}`}</span>
      );
    }
  };

  const questionMark = () => {
    const tooltip = (
      <Tooltip id="tooltip">
        Get faster application results! This school through Unify responds in weeks, not months like
        other schools. Apply now for a quick response.
      </Tooltip>
    );
    return (
      <OverlayTrigger placement="bottom" overlay={tooltip}>
        <div className="question-mark">?</div>
      </OverlayTrigger>
    );
  };

  const expressApply = () => {
    return (
      <div className="d-flex gap-2 align-items-center">
        <span className="express_apply">Express Apply</span>
        {questionMark()}
      </div>
    );
  };

  const preferencesButton = () => {
    const label = programLabelDetail(
      !school.best_preferences_score && school.best_preferences_score !== 0
        ? undefined
        : school.best_preferences_score * 100
    );
    if (label == undefined) {
      return (
        <Link to="/profile" className="btn preference-btn">
          Find your match
        </Link>
      );
    } else {
      return preferenceButtonInfo(label);
    }
  };

  const preferenceButtonInfo = (label: string) => {
    if (label == 'High') {
      return (
        <div className="green d-flex gap-2 align-items-center preference-fill-btn justify-content-center fw-bold">
          <img src={HighFace} />
          Good match
        </div>
      );
    } else if (label == 'Fair') {
      return (
        <div className="yellow d-flex gap-2 align-items-center preference-fill-btn justify-content-center fw-bold">
          <img src={FairFace} />
          Fair match
        </div>
      );
    } else {
      return (
        <div className="orange d-flex gap-2 align-items-center preference-fill-btn justify-content-center fw-bold">
          <img src={LowFace} />
          Low match
        </div>
      );
    }
  };

  return (
    <div className={`col ${style.uaSchool}`}>
      <div className="card h-100 ua-school__card p-relative overflow-hidden shadow-sm text-decoration-none">
        {discountLabel()}
        <div
          className="ua-school__card-head d-flex justify-content-end align-items-end"
          style={{
            backgroundImage: `url(${school.photos?.[0]})`,
          }}
        >
          <div className="ua-school_card-head-row d-flex align-items-center row mx-0 w-100 ps-3 pb-3">
            <div className="ua-school__card-head-logo d-flex align-items-center justify-content-center bg-white rounded-2 overflow-hidden col-3 p-0">
              <img className="w-100" src={school.logo} />
            </div>
            <div className="ua-school_card-head-info d-flex flex-column col-9 ps-3">
              <h5 className="card-title text-truncate d-block text-start">{school.name}</h5>
              <p className="text-start text-white fw-600 my-0 fs-8">
                <i className="bi bi-geo-alt" /> {schoolCampuses()}
              </p>
            </div>
          </div>
        </div>
        {schoolRanking()}
        <div className="card-body">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-cash"></i>
            <div className="d-flex flex-column">
              <p className="mb-0 fs-8 label">Tuition Fees</p>
              <p className="text-center blue-text fw-semibold mb-0"> {school.tuition_range} </p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-calendar3-week"></i>
            <div className="d-flex flex-column">
              <p className="mb-0 fs-8 label">Intake month</p>
              <div className="d-flex align-items-center gap-2">
                {intakeMonths()}
                {school?.express_apply && expressApply()}
              </div>
            </div>
          </div>
          <div className="d-flex gap-3 mt-3">
            <button className="btn read-more-btn" type="button" onClick={handleShowModal}>
              Read more
            </button>
            {preferencesButton()}
          </div>
        </div>
      </div>
      <SchoolCardModal schoolId={school.id} showModal={showModal} setShowModal={setShowModal} />
    </div>
  );
};

export default SchoolCard;
